import React from 'react';
import Icon from '../../../components/AppIcon';
import { formatDistanceToNow } from 'date-fns';

const MessageInbox = ({ threads, selectedThread, onThreadSelect, loading, currentUserId }) => {
  const getLastMessage = (thread) => {
    const messages = thread?.messages || [];
    return messages?.[messages?.length - 1];
  };

  const getUnreadCount = (thread) => {
    const participant = thread?.participants?.find(p => p?.user_id === currentUserId);
    const lastRead = participant?.last_read_at ? new Date(participant?.last_read_at) : null;
    const lastUpdate = thread?.updated_at ? new Date(thread?.updated_at) : null;
    return !lastRead || (lastUpdate && lastUpdate > lastRead) ? 1 : 0;
  };

  const getOtherParticipants = (thread) => {
    return thread?.participants
      ?.filter(p => p?.user_id !== currentUserId)
      ?.map(p => p?.user_profile?.full_name)
      ?.join(', ') || 'Unknown';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      member: 'bg-blue-500/10 text-blue-600',
      trainer: 'bg-purple-500/10 text-purple-600',
      staff: 'bg-green-500/10 text-green-600',
      manager: 'bg-orange-500/10 text-orange-600',
      owner: 'bg-red-500/10 text-red-600'
    };
    return colors?.[role] || 'bg-muted text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl p-6 shadow-md border border-border">
        <div className="space-y-4">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-16 bg-muted rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (threads?.length === 0) {
    return (
      <div className="bg-card rounded-xl p-6 shadow-md border border-border">
        <div className="text-center py-8">
          <Icon name="Inbox" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No messages yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-md border border-border overflow-hidden">
      <div className="p-4 border-b border-border">
        <h2 className="font-semibold text-foreground">Inbox</h2>
        <p className="text-xs text-muted-foreground mt-1">{threads?.length} conversations</p>
      </div>
      <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
        {threads?.map((thread) => {
          const lastMessage = getLastMessage(thread);
          const unreadCount = getUnreadCount(thread);
          const isSelected = selectedThread?.id === thread?.id;
          const otherParticipant = thread?.participants?.find(p => p?.user_id !== currentUserId)?.user_profile;

          return (
            <button
              key={thread?.id}
              onClick={() => onThreadSelect(thread)}
              className={`
                w-full p-4 text-left transition-colors hover:bg-muted
                ${isSelected ? 'bg-primary/5 border-l-4 border-primary' : ''}
              `}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-medium text-foreground text-sm truncate">
                      {getOtherParticipants(thread)}
                    </h3>
                    {unreadCount > 0 && (
                      <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0"></span>
                    )}
                  </div>
                  {otherParticipant && (
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full mb-1 ${getRoleBadgeColor(otherParticipant?.role)}`}>
                      {otherParticipant?.role}
                    </span>
                  )}
                  <p className="text-xs font-medium text-foreground mb-1 truncate">
                    {thread?.subject}
                  </p>
                  {lastMessage && (
                    <>
                      <p className="text-xs text-muted-foreground truncate">
                        {lastMessage?.content}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(lastMessage?.created_at), { addSuffix: true })}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MessageInbox;
